import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useResponsive } from './ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import {
  GetTicketsCountByRoofTopResponseDTO,
  GetTicketsRequestParamsDTO,
  GetTicketsResponseDTO,
} from 'declarations/dto/tickets';
import { SortingOrderE } from 'declarations/enums/common';
import {
  AgeFilterValuesE,
  ParameterSortingValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
  TicketsRefinementKeysE,
  TicketsViewModeE,
} from 'declarations/enums/tickets';
import { RoofTopI } from 'declarations/interfaces/roofTop';
import { TicketCountByRoofTopI } from 'declarations/interfaces/tickets';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTickets, useFetchTicketsCountByRoofTop } from 'hooks/api/useTickets';

export type TicketsContextT = {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  ticketsCount: number;
  ticketsCountByRoofTop: GetTicketsCountByRoofTopResponseDTO | null | undefined;
  mappedRooftopsList: TicketCountByRoofTopI[] | null | undefined;
  isTicketsFetchingError: boolean;
  fetchTicketsErrorMessage: string | undefined;
  fetchTicketsValidationErrorMessage: string | undefined;
  isTicketsFetchingCountByRoofTopError: boolean;
  isSuccessfullyFetchedTickets: boolean;
  isLoadingTickets: boolean;
  isLoadingTicketsCountByRoofTop: boolean;
  setSidebarIsOpen: (isSidebarOpened: boolean) => void;
  isSidebarOpened: boolean;
  setTicketsViewMode: (ticketsViewMode: TicketsViewModeE) => void;
  ticketsViewMode: TicketsViewModeE;
  setTicketId: (id: string | null) => void;
  selectedTicketId: string | null;
  isNewTicketPopupOpened: boolean;
  setNewTicketPopupVisibility: (isOpened: boolean) => void;
  isROListPopupOpened: boolean;
  setROListPopupVisibility: (isOpened: boolean) => void;
  getSearchParam: (key: TicketsRefinementKeysE) => string | null;
  updateSearchParam: (key: TicketsRefinementKeysE, value: string) => void;
  clearSearchParam: (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]) => void;
  isFetchingNextTicketsPage: boolean;
  isFetchingTickets: boolean;
  hasNextTicketsPage: boolean | undefined;
  fetchNextTicketsPage: () => void;
  fetchTicketsParams: GetTicketsRequestParamsDTO;
  activeScheduleCards: string[];
  setActiveScheduleCards: (cards: string[]) => void;
  activeScheduleFilters: ScheduleFilterValuesE[];
  setActiveScheduleFilters: (flters: ScheduleFilterValuesE[]) => void;
  getAllSearchParam: (key: TicketsRefinementKeysE) => number[];
  updateSearchParamsArray: (
    key: TicketsRefinementKeysE,
    value: (number | ScheduleFilterValuesE)[] | null
  ) => void;
  selectedRoofTop: string;
  scheduleCategoryId: number;
  setScheduleCategoryId: (id: number) => void;
  dealerSelectedRoofTopCount: number;
  setDealerSelectedRoofTopCount: (count: number) => void;
  dealerSelectedRooftopIds: string | null;
  setDealerSelectedRooftopIds: (id: string | null) => void;
  initialSelectedRooftopIds: string[];
  setInitialSelectedRooftopIds: (ids: string[]) => void;
  roofTopsList: RoofTopI[] | null;
  shouldLoadMoreRooftops: boolean;
  hasNextRooftopsPage: boolean;
  fetchNextRooftopsPage: () => void;
  selectedTicketsIds: string[];
  setSelectedTicketsIds: (id: string[]) => void;
};

type TicketsProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  ticketsPages: null,
  ticketsCount: 0,
  ticketsCountByRoofTop: null,
  mappedRooftopsList: null,
  isTicketsFetchingError: false,
  fetchTicketsErrorMessage: undefined,
  fetchTicketsValidationErrorMessage: undefined,
  isTicketsFetchingCountByRoofTopError: false,
  isSuccessfullyFetchedTickets: false,
  isLoadingTickets: false,
  isLoadingTicketsCountByRoofTop: false,
  setSidebarIsOpen: () => undefined,
  isSidebarOpened: true,
  setTicketsViewMode: () => undefined,
  ticketsViewMode: TicketsViewModeE.List,
  setTicketId: () => undefined,
  selectedTicketId: '',
  isNewTicketPopupOpened: false,
  setNewTicketPopupVisibility: () => undefined,
  isROListPopupOpened: false,
  setROListPopupVisibility: () => undefined,
  getSearchParam: () => null,
  updateSearchParam: () => undefined,
  clearSearchParam: () => undefined,
  isFetchingNextTicketsPage: false,
  isFetchingTickets: false,
  hasNextTicketsPage: false,
  fetchNextTicketsPage: () => null,
  fetchTicketsParams: {},
  activeScheduleCards: [],
  setActiveScheduleCards: () => undefined,
  activeScheduleFilters: [],
  setActiveScheduleFilters: () => undefined,
  getAllSearchParam: () => [],
  updateSearchParamsArray: () => undefined,
  selectedRoofTop: '',
  scheduleCategoryId: 0,
  setScheduleCategoryId: () => undefined,
  dealerSelectedRoofTopCount: 0,
  setDealerSelectedRoofTopCount: () => undefined,
  dealerSelectedRooftopIds: '',
  setDealerSelectedRooftopIds: () => undefined,
  initialSelectedRooftopIds: [],
  setInitialSelectedRooftopIds: () => undefined,
  roofTopsList: null,
  shouldLoadMoreRooftops: false,
  hasNextRooftopsPage: false,
  fetchNextRooftopsPage: () => undefined,
  selectedTicketsIds: [],
  setSelectedTicketsIds: () => undefined,
};

export const TicketsContext = createContext<TicketsContextT>(contextDefaultValue);

const TicketsProvider: FC<TicketsProviderPropsT> = ({ children }) => {
  /**
   * Hooks
   */
  const [ticketsViewMode, setTicketsViewMode] = useState<TicketsViewModeE>(TicketsViewModeE.List);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [isNewTicketPopupOpened, setNewTicketPopupVisibility] = useState<boolean>(false);
  const [isROListPopupOpened, setROListPopupVisibility] = useState<boolean>(false);
  let [searchParams, setSearchParams] = useSearchParams();
  const [selectedRoofTop, setSelectedRoofTop] = useState<string>('');
  const navigate = useNavigate();
  const [activeScheduleCards, setActiveScheduleCards] = useState<string[]>([]);
  const [activeScheduleFilters, setActiveScheduleFilters] = useState<ScheduleFilterValuesE[]>([]);
  const [defaultRooftopId, setDefaultRooftopId] = useState<string>();
  const [scheduleCategoryId, setScheduleCategoryId] = useState<number>(0);
  const [dealerSelectedRoofTopCount, setDealerSelectedRoofTopCount] = useState(0);
  const [dealerSelectedRooftopIds, setDealerSelectedRooftopIds] = useState<string | null>('');
  const [initialSelectedRooftopIds, setInitialSelectedRooftopIds] = useState<string[]>([]);
  const [selectedTicketsIds, setSelectedTicketsIds] = useState<string[]>([]);

  /**
   * Variables
   */
  const { isMobile } = useResponsive();
  const [isSidebarOpened, setSidebarIsOpen] = useState(true);
  const currentPage = 1;
  const ROOFTOPS_PAGE_SIZE = 25;
  const ticketsPerPage = 25;
  const fetchTicketsParams = {
    [TicketsRefinementKeysE.RoofTopId]:
      searchParams.get(TicketsRefinementKeysE.RoofTopId) || undefined,
    [TicketsRefinementKeysE.Status]:
      (searchParams.get(TicketsRefinementKeysE.Status) as StatusFilterValuesE) || undefined,
    [TicketsRefinementKeysE.TicketAge]:
      (searchParams.get(TicketsRefinementKeysE.TicketAge) as AgeFilterValuesE) || undefined,
    [TicketsRefinementKeysE.RONumber]:
      searchParams.get(TicketsRefinementKeysE.RONumber) || undefined,
    [TicketsRefinementKeysE.SearchTerm]:
      searchParams.get(TicketsRefinementKeysE.SearchTerm) || undefined,
    [TicketsRefinementKeysE.AssignedToMe]:
      !!searchParams.get(TicketsRefinementKeysE.AssignedToMe) || undefined,
    [TicketsRefinementKeysE.PastMonth]:
      !!searchParams.get(TicketsRefinementKeysE.PastMonth) || undefined,
    [TicketsRefinementKeysE.Past30Days]:
      !!searchParams.get(TicketsRefinementKeysE.Past30Days) || undefined,
    [TicketsRefinementKeysE.IsArchived]:
      !!searchParams.get(TicketsRefinementKeysE.IsArchived) || false,
    [TicketsRefinementKeysE.Parameter]:
      (searchParams.get(TicketsRefinementKeysE.Parameter) as ParameterSortingValuesE) || undefined,
    [TicketsRefinementKeysE.Order]:
      (searchParams.get(TicketsRefinementKeysE.Order) as SortingOrderE) || undefined,
    [TicketsRefinementKeysE.CurrentPage]:
      (searchParams.get(TicketsRefinementKeysE.CurrentPage) as string) || currentPage.toString(),
    [TicketsRefinementKeysE.CountPerPage]:
      (searchParams.get(TicketsRefinementKeysE.CountPerPage) as string) ||
      ticketsPerPage.toString(),
    [TicketsRefinementKeysE.Schedule]:
      (searchParams.get(TicketsRefinementKeysE.Schedule) as ScheduleFilterValuesE) || undefined,
    [TicketsRefinementKeysE.Category]:
      searchParams.get(TicketsRefinementKeysE.Category) || undefined,
    [TicketsRefinementKeysE.SubCategory]:
      searchParams.get(TicketsRefinementKeysE.SubCategory) || undefined,
  };
  const getSearchParam = useCallback(
    (key: TicketsRefinementKeysE) => searchParams.get(key),
    [searchParams]
  );
  const fetchCountByRooftopsParams = useMemo(
    () => ({
      paging: {
        currentPage: currentPage,
        countPerPage: ROOFTOPS_PAGE_SIZE,
      },
      roofTopIds: dealerSelectedRooftopIds,
    }),
    [currentPage, ROOFTOPS_PAGE_SIZE, dealerSelectedRooftopIds]
  );
  /**
   * Api hooks
   */
  const { userProfile } = useUserProfile();
  const ticketsResult = useFetchTickets(fetchTicketsParams);
  const ticketsCountByRoofTopResult = useFetchTicketsCountByRoofTop(fetchCountByRooftopsParams);
  const categoriesResult = useFetchTicketRequestCategories(
    true,
    selectedRoofTop ?? defaultRooftopId
  );
  /**
   * Variables
   */
  const fetchTicketsErrorMessage = useMemo(
    () => getErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );
  const fetchTicketsValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );

  /**
   * Handlers
   */
  const getAllSearchParam = useCallback(
    (key: TicketsRefinementKeysE) => searchParams.getAll(key).map(Number),
    [searchParams]
  );

  const updateSearchParam = useCallback(
    (key: TicketsRefinementKeysE, value: string) => {
      searchParams.set(key, value);
      setSearchParams(searchParams);
      if (isMobile) {
        setSidebarIsOpen(false);
      }
    },
    [isMobile, searchParams, setSearchParams, setSidebarIsOpen]
  );
  const clearSearchParam = useCallback(
    (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]) => {
      if (Array.isArray(keys)) {
        keys.forEach(key => {
          searchParams.delete(key);
        });
      }

      if (typeof keys === 'string') {
        searchParams.delete(keys);
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchParamsArray = useCallback(
    (key: TicketsRefinementKeysE, valuesArray: (number | ScheduleFilterValuesE)[] | null) => {
      let queryString = window.location.pathname + '?';
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.forEach((value, paramKey) => {
        if (paramKey !== key) {
          queryString += `${paramKey}=${encodeURIComponent(value)}&`;
        }
      });

      if (valuesArray && valuesArray.length > 0) {
        const valuesString = valuesArray.join(',');
        queryString += `${key}=${encodeURIComponent(valuesString)}`;
      } else {
        queryString = queryString.replace(/&$/, '');
      }
      navigate(queryString, { replace: true });
    },
    [navigate]
  );

  /**
   * Effects
   */
  useEffect(() => {
    setSelectedRoofTop(getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? '');
  }, [getSearchParam]);

  useEffect(() => {
    if (ticketsCountByRoofTopResult.data && ticketsCountByRoofTopResult.data.pages.length > 0) {
      const countByRoofTops = ticketsCountByRoofTopResult.data.pages.flatMap(
        (page: any) => page?.countByRoofTop
      );

      if (countByRoofTops.length > 0) {
        setDefaultRooftopId(countByRoofTops[0]?.roofTopId);
      }
    }
  }, [ticketsCountByRoofTopResult.data]);

  useEffect(() => {
    if (defaultRooftopId) {
      categoriesResult.refetch().then(() => {
        const scheduleCategory = categoriesResult?.data?.filter(
          category => category.categoryName.toLowerCase() === 'schedule'
        );
        const categoryIdsString = searchParams
          .getAll(TicketsRefinementKeysE.Category)[0]
          ?.split(',');
        // Check if schedule category ID is in categoryIdsString and no schedule ranges in searchParams
        const scheduleCategoryIdArr = scheduleCategory?.map(category => category.id.toString());
        setScheduleCategoryId(Number(scheduleCategoryIdArr ? scheduleCategoryIdArr[0] : undefined));
        const hasScheduleCategoryId = categoryIdsString?.some(id =>
          scheduleCategoryIdArr?.includes(id)
        );
        const hasScheduleRanges = searchParams.getAll(TicketsRefinementKeysE.Schedule).length > 0;

        if (hasScheduleCategoryId && !hasScheduleRanges) {
          setActiveScheduleCards([
            'schedule-all',
            'dealer-1',
            'dealer-2',
            'dealer-3',
            'dealer-4',
            'dealer-5',
          ]);
        }
      });
    }
    // eslint-disable-next-line
  }, [defaultRooftopId, searchParams]);

  const combinedTicketsCountByRoofTop = useMemo(() => {
    if (ticketsCountByRoofTopResult.data?.pages) {
      return ticketsCountByRoofTopResult.data.pages.reduce<TicketCountByRoofTopI[]>((acc, page) => {
        return acc.concat(page?.countByRoofTop);
      }, []);
    }
    return [];
  }, [ticketsCountByRoofTopResult.data]);

  return (
    <TicketsContext.Provider
      value={{
        ticketsPages: ticketsResult.data?.pages ?? null,
        ticketsCount: ticketsResult.data?.pages[0].count || 0,
        isTicketsFetchingError: ticketsResult.isError,
        fetchTicketsErrorMessage,
        fetchTicketsValidationErrorMessage,
        mappedRooftopsList: combinedTicketsCountByRoofTop,
        isLoadingTickets: ticketsResult.isLoading || ticketsResult.isRefetching,
        isSuccessfullyFetchedTickets: ticketsResult.isSuccess,
        ticketsCountByRoofTop: ticketsCountByRoofTopResult.data?.pages[0],
        isTicketsFetchingCountByRoofTopError: ticketsCountByRoofTopResult.isError,
        isLoadingTicketsCountByRoofTop:
          ticketsCountByRoofTopResult.isLoading || ticketsCountByRoofTopResult.isFetching,
        setSidebarIsOpen,
        isSidebarOpened: isSidebarOpened,
        setTicketsViewMode,
        ticketsViewMode,
        setTicketId,
        selectedTicketId: ticketId,
        isNewTicketPopupOpened,
        setNewTicketPopupVisibility,
        isROListPopupOpened,
        setROListPopupVisibility,
        getSearchParam,
        updateSearchParam,
        getAllSearchParam,
        updateSearchParamsArray,
        clearSearchParam,
        isFetchingNextTicketsPage: ticketsResult.isFetchingNextPage,
        isFetchingTickets: ticketsResult.isFetching,
        fetchNextTicketsPage: ticketsResult.fetchNextPage,
        hasNextTicketsPage: ticketsResult.hasNextPage,
        fetchTicketsParams,
        activeScheduleCards,
        setActiveScheduleCards,
        activeScheduleFilters,
        setActiveScheduleFilters,
        selectedRoofTop,
        scheduleCategoryId,
        setScheduleCategoryId,
        dealerSelectedRoofTopCount,
        setDealerSelectedRoofTopCount,
        setDealerSelectedRooftopIds,
        dealerSelectedRooftopIds,
        initialSelectedRooftopIds,
        setInitialSelectedRooftopIds,
        roofTopsList: userProfile?.mappedRoofTops || null,
        hasNextRooftopsPage: Boolean(ticketsCountByRoofTopResult.hasNextPage),
        fetchNextRooftopsPage: ticketsCountByRoofTopResult.fetchNextPage,
        shouldLoadMoreRooftops: Boolean(
          ticketsCountByRoofTopResult.isRefetching &&
            ticketsCountByRoofTopResult.isFetchingNextPage &&
            ticketsCountByRoofTopResult.hasNextPage
        ),
        selectedTicketsIds,
        setSelectedTicketsIds,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
};

const useTickets = () => useContext(TicketsContext);

export { TicketsProvider, useTickets };
